import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;
const OFFER_CART_RESOLVED_EVENT = 'offer-engine:cart-resolved';
const SUBMIT_CONTROL_UNLOCK_TIMEOUT = 15000;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */
 
/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Shows confirmation only after the product form confirms a successful add.
   * @param {SubmitEvent} event - The successful form submission.
   */
  handleAddSuccess(event) {
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {Array<{ control: HTMLButtonElement | HTMLInputElement, wasDisabled: boolean }> | undefined} */
  #submitControlState;

  /** @type {number | undefined} */
  #submitControlUnlockTimeout;

  /** @type {AbortController | undefined} */
  #submitControlUnlockController;

  /** @type {Array<{quantity: number, variant_id: number, properties?: Record<string, string | boolean>}>} */
  #cartItems = [];

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
    document.addEventListener(OFFER_CART_RESOLVED_EVENT, this.#onOfferCartResolved, { signal });
    this.addEventListener('change', this.#onPreorderToggleChange, { signal });
    this.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantitySelectorUpdate, { signal });
    this.#syncPreorderControls();
    this.#syncOfferPriceDisplay();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#enableSubmitControls();
    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    this.#cartItems = cart.items;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);
    this.#syncGiftDisplay({ cart });
    this.#syncOfferPriceDisplay({ cart });

    return cartQty;
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      return this.#updateCartQuantityFromData(cart);
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
      this.#syncPreorderControls({ cart });
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /**
   * Syncs preorder UI after offer-engine has resolved cart state.
   * @param {CustomEvent} event
   */
  #onOfferCartResolved = (event) => {
    const cart = event.detail?.cart;
    this.#syncPreorderControls({ cart });
    this.#syncGiftDisplay({ cart });
    this.#syncOfferPriceDisplay({ cart });
  };

  /**
   * Updates gift display progress when the active product-form quantity changes.
   * @param {CustomEvent} event
   */
  #onQuantitySelectorUpdate = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('quantity-selector-component')) return;

    this.dataset.quantityOverrideDirty = 'true';
    this.#syncGiftDisplay();
    this.#syncOfferPriceDisplay();
  };

  /**
   * Updates form visuals without mutating cart state.
   * @param {Event} event
   */
  #onPreorderToggleChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.dataset.content !== 'preorder-toggle-input') return;

    this.dataset.preorderToggleDirty = 'true';
    this.#syncPreorderControls({ preferredChecked: target.checked });
  };

  /**
   * @param {ParentNode} [root]
   * @returns {HTMLInputElement | null}
   */
  #getPreorderToggleInput(root = this) {
    return /** @type {HTMLInputElement | null} */ (root.querySelector('[data-content="preorder-toggle-input"]'));
  }

  #disablePreorderToggle() {
    const toggle = this.#getPreorderToggleInput();
    if (toggle) toggle.disabled = true;
  }

  #disableSubmitControls() {
    if (this.#submitControlState) return;

    const controls = /** @type {Array<HTMLButtonElement | HTMLInputElement>} */ (
      this.#getAddToCartButtonContainers()
        .map((container) => container.refs.addToCartButton)
        .filter(Boolean)
    );
    const toggle = this.#getPreorderToggleInput();
    if (toggle) controls.push(toggle);

    this.#submitControlState = controls.map((control) => ({
      control,
      wasDisabled: control.disabled,
    }));

    for (const control of controls) {
      control.disabled = true;
    }
  }

  #enableSubmitControls() {
    if (!this.#submitControlState) return;

    for (const { control, wasDisabled } of this.#submitControlState) {
      if (!wasDisabled) control.disabled = false;
    }

    this.#submitControlState = undefined;

    if (this.#submitControlUnlockTimeout) {
      clearTimeout(this.#submitControlUnlockTimeout);
      this.#submitControlUnlockTimeout = undefined;
    }

    this.#submitControlUnlockController?.abort();
    this.#submitControlUnlockController = undefined;

    this.#syncPreorderControls({ preferredChecked: this.#getPreorderToggleInput()?.checked });
    this.#setCartLoadingState(false);
  }

  /**
   * @param {boolean} isLoading
   */
  #setCartLoadingState(isLoading) {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');

    cartItemsComponents.forEach((item) => {
      if (!(item instanceof HTMLElement)) return;
      item.classList.toggle('cart-items-disabled', isLoading);
    });
  }

  #enableSubmitControlsOnOfferCartResolved() {
    this.#submitControlUnlockController?.abort();
    this.#submitControlUnlockController = new AbortController();

    const handleResolved = () => {
      this.#enableSubmitControls();
    };

    document.addEventListener(OFFER_CART_RESOLVED_EVENT, handleResolved, {
      once: true,
      signal: this.#submitControlUnlockController.signal,
    });

    this.#submitControlUnlockTimeout = setTimeout(handleResolved, SUBMIT_CONTROL_UNLOCK_TIMEOUT);
  }

  /** @param {SubmitEvent} event */
  #handleAddSuccess(event) {
    this.refs.addToCartTextError?.classList.add('hidden');
    const container = /** @type {AddToCartComponent | null} */ (
      event.submitter?.closest('add-to-cart-component') || null
    );
    container?.handleAddSuccess(event);
    this.dispatchEvent(new CustomEvent('product-form:add-success', { bubbles: true }));
  }

  /**
   * @param {object | undefined} cart
   * @returns {boolean}
   */
  #checkCartPreorderActive(cart) {
    const attributes = cart?.attributes || window.cartAttributes || window.config?.params?.cart_attributes || {};
    return attributes._preorder === true || attributes._preorder === 'true';
  }

  /**
   * @param {boolean} isPreorder
   */
  #updateAddToCartText(isPreorder) {
    const toggle = this.#getPreorderToggleInput();
    if (!toggle) return;

    const addText = toggle?.dataset.addToCartText || 'Add to cart';
    const preorderText = toggle?.dataset.addToPreorderText || 'Add to preorder';
    const nextText = isPreorder ? preorderText : addText;

    for (const container of this.#getAddToCartButtonContainers()) {
      const button = container.refs.addToCartButton;
      if (!button || button.disabled) continue;

      const label =
        button.querySelector('[data-content="add-to-cart-label"] > span > span') ||
        button.querySelector('[data-content="add-to-cart-label"]');
      if (label) label.textContent = nextText;
    }
  }

  /**
   * Updates the product details price scope to match this form's preorder state.
   * @param {boolean | undefined} isPreorder
   */
  #updateProductDetailsPreorderState(isPreorder) {
    const productDetails = this.closest('.product-details');
    if (!productDetails) return;

    if (typeof isPreorder !== 'boolean') {
      productDetails.removeAttribute('data-form-preorder-selected');
      return;
    }

    productDetails.setAttribute('data-form-preorder-selected', String(isPreorder));
  }

  /**
   * Clears local form discount display overrides.
   * @param {HTMLElement} productDetails
   */
  #clearProductDetailsDiscountDisplayState(productDetails) {
    productDetails.removeAttribute('data-form-quantity-override');

    for (const attr of Array.from(productDetails.attributes)) {
      if (attr.name.startsWith('data-form-set-') && attr.name.endsWith('-display')) {
        productDetails.removeAttribute(attr.name);
      }
    }
  }

  /**
   * @param {string | undefined} value
   * @returns {number[]}
   */
  #parseDiscountVariantIds(value) {
    return (value || '')
      .split('|')
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0);
  }

  /**
   * @param {Array<{ discountId: string, threshold: number }>} entries
   * @param {number} cartQuantity
   * @param {number} effectiveQuantity
   * @returns {string}
   */
  #selectFormDiscountDisplayId(entries, cartQuantity, effectiveQuantity) {
    const sorted = entries
      .filter((entry) => entry.discountId && Number.isFinite(entry.threshold))
      .sort((a, b) => a.threshold - b.threshold);
    if (!sorted.length) return '';

    const crossed = sorted.filter((entry) => entry.threshold > cartQuantity && entry.threshold <= effectiveQuantity);
    if (crossed.length) return crossed[crossed.length - 1].discountId;

    const next = sorted.find((entry) => cartQuantity < entry.threshold);
    return next?.discountId || sorted[sorted.length - 1].discountId;
  }

  /**
   * Updates local price discount selection from cart quantity plus this form's selected quantity.
   * @param {{ cart?: Cart }} [options]
   */
  #syncOfferPriceDisplay(options = {}) {
    if (options.cart?.items) {
      this.#cartItems = options.cart.items;
    }

    const productDetails = this.closest('.product-details');
    if (!(productDetails instanceof HTMLElement)) return;

    this.#clearProductDetailsDiscountDisplayState(productDetails);
    if (this.dataset.quantityOverrideDirty !== 'true') return;

    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    const activeQuantity = Number(quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
    const activeVariantId = Number(this.refs.variantId?.value) || 0;
    const snippets = Array.from(
      productDetails.querySelectorAll(
        '.discount-price--snippet[data-discount-set][data-discount-id][data-minimum-threshold]'
      )
    );
    if (!quantitySelector || !activeVariantId || !snippets.length) return;

    const snippetsBySet = new Map();
    snippets.forEach((snippet) => {
      if (!(snippet instanceof HTMLElement)) return;

      const setKey = snippet.dataset.discountSet || '';
      const discountId = snippet.dataset.discountId || '';
      const threshold = Number(snippet.dataset.minimumThreshold);
      const variantIds = this.#parseDiscountVariantIds(snippet.dataset.discountVariantIds);
      if (!setKey || !discountId || !Number.isFinite(threshold) || !variantIds.length) return;

      const entries = snippetsBySet.get(setKey) || new Map();
      if (!entries.has(discountId)) {
        entries.set(discountId, { discountId, threshold, variantIds });
      }
      snippetsBySet.set(setKey, entries);
    });

    const cartItems = this.#cartItems.length
      ? this.#cartItems
      : Array.isArray(window.cartItems)
        ? window.cartItems
        : [];
    const activeVariantCartQuantity = Number(
      this.querySelector('quantity-selector-component input[ref="quantityInput"]')?.getAttribute('data-cart-quantity')
    ) || 0;
    let hasQuantityOverride = false;

    snippetsBySet.forEach((entryMap, setKey) => {
      const entries = Array.from(entryMap.values());
      const variantIdSet = new Set(entries.flatMap((entry) => entry.variantIds));
      if (!variantIdSet.has(activeVariantId)) return;

      const cartQuantity = cartItems.length
        ? cartItems
          .filter((item) => variantIdSet.has(Number(item.variant_id)) && !this.#isGiftCartItem(item))
          .reduce((sum, item) => sum + (Number(item.quantity) || 0), 0)
        : activeVariantCartQuantity;
      const effectiveQuantity = cartQuantity + activeQuantity;
      const displayId = this.#selectFormDiscountDisplayId(entries, cartQuantity, effectiveQuantity);

      if (displayId) {
        productDetails.setAttribute(`data-form-${setKey}-display`, displayId);
        hasQuantityOverride = true;
      }
    });

    if (hasQuantityOverride) {
      productDetails.setAttribute('data-form-quantity-override', 'true');
    }
  }

  /**
   * @param {{ cart?: object, preferredChecked?: boolean }} [options]
   */
  #syncPreorderControls(options = {}) {
    const toggle = this.#getPreorderToggleInput();
    const shouldPreserveLocalToggle =
      options.cart &&
      this.dataset.preorderToggleDirty === 'true' &&
      toggle &&
      !toggle.disabled;
    const cartPreorderActive = this.#checkCartPreorderActive(options.cart);
    const nextChecked = shouldPreserveLocalToggle
      ? toggle.checked
      : toggle?.disabled
      ? cartPreorderActive
      : typeof options.preferredChecked === 'boolean'
        ? options.preferredChecked
        : cartPreorderActive;

    if (toggle) {
      toggle.checked = nextChecked;
      toggle.closest('[data-preorder-toggle]')?.setAttribute('data-state', nextChecked ? 'active' : 'inactive');
    }

    this.#updateProductDetailsPreorderState(toggle ? nextChecked : undefined);
    this.#updateAddToCartText(nextChecked);
  }

  /**
   * @param {Document | Element} html
   */
  #morphPreorderToggle(html) {
    const currentToggle = this.querySelector('[data-preorder-toggle]');
    const newToggle = html.querySelector('[data-preorder-toggle]');

    if (currentToggle && newToggle) {
      morph(currentToggle, newToggle);
    } else if (currentToggle && !newToggle) {
      currentToggle.remove();
    } else if (!currentToggle && newToggle && this.refs.productFormButtons) {
      this.refs.productFormButtons.insertBefore(newToggle.cloneNode(true), this.refs.productFormButtons.firstElementChild);
    }
  }

  /**
   * @param {Document | Element} html
   */
  #morphGiftDisplay(html) {
    const currentDisplay = this.querySelector('[data-content="gift-display"][data-location="buy-buttons"]');
    const newDisplay = html.querySelector('[data-content="gift-display"][data-location="buy-buttons"]');
    const currentWrapper = currentDisplay?.parentElement?.classList.contains('mt1')
      ? currentDisplay.parentElement
      : currentDisplay;
    const newWrapper = newDisplay?.parentElement?.classList.contains('mt1')
      ? newDisplay.parentElement
      : newDisplay;

    if (currentWrapper && newWrapper) {
      morph(currentWrapper, newWrapper);
    } else if (currentWrapper && !newWrapper) {
      currentWrapper.remove();
    } else if (!currentWrapper && newWrapper && this.refs.productFormButtons) {
      const insertAfter = this.refs.acceleratedCheckoutButtonContainer || this.refs.productFormButtons.lastElementChild;
      const clonedWrapper = /** @type {Element} */ (newWrapper.cloneNode(true));

      if (insertAfter) {
        insertAfter.insertAdjacentElement('afterend', clonedWrapper);
      } else {
        this.refs.productFormButtons.appendChild(clonedWrapper);
      }
    }
  }

  /**
   * Handles the submit event for the product form.
 *
 * @param {Event} event - The submit event.
 */
  async handleSubmit(event) {
  const { addToCartTextError } = this.refs;
  // Stop default behaviour from the browser
  event.preventDefault();

  if (this.#timeout) clearTimeout(this.#timeout);
  // Query for ALL add-to-cart components
  const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
    this.querySelectorAll('add-to-cart-component')
  );

  // Check if ANY add to cart button is disabled and do an early return if it is
  const anyButtonDisabled = Array.from(allAddToCartContainers).some(
    (container) => container.refs.addToCartButton?.disabled
  );
  if (anyButtonDisabled) return;

  // Send the add to cart information to the cart
  const form = this.querySelector('form');
  if (!form) throw new Error('Product form element missing');

  if (this.refs.quantitySelector?.canAddToCart) {
    const validation = this.refs.quantitySelector.canAddToCart();
    if (!validation.canAdd) {
      // Disable ALL add-to-cart buttons
      for (const container of allAddToCartContainers) {
        container.disable();
      }

      const errorTemplate = this.dataset.quantityErrorMax || '';
      const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
      if (addToCartTextError) {
        addToCartTextError.classList.remove('hidden');

        const textNode = addToCartTextError.childNodes[2];
        if (textNode) {
          textNode.textContent = errorMessage;
        } else {
          const newTextNode = document.createTextNode(errorMessage);
          addToCartTextError.appendChild(newTextNode);
        }

        this.#setLiveRegionText(errorMessage);

        if (this.#timeout) clearTimeout(this.#timeout);
        this.#timeout = setTimeout(() => {
          if (!addToCartTextError) return;
          addToCartTextError.classList.add('hidden');
          this.#clearLiveRegionText();
        }, ERROR_MESSAGE_DISPLAY_DURATION);
      }

      setTimeout(() => {
        // Re-enable ALL add-to-cart buttons
        for (const container of allAddToCartContainers) {
          container.enable();
        }
      }, ERROR_BUTTON_REENABLE_DELAY);

      return;
    }
  }

  const formData = new FormData(form);
  this.#disableSubmitControls();
  this.#setCartLoadingState(true);
  this.dataset.preorderToggleDirty = 'false';
  let attrPropContent;

  try {
    attrPropContent = await window.offerEngine.handleAddPayloadData(event.submitter, formData);
    if (attrPropContent?.no_op === true) {
      this.#enableSubmitControlsOnOfferCartResolved();
      const added = await attrPropContent.completion;
      if (!added) throw new Error('Managed cart add failed');
    }
  } catch (error) {
    this.#enableSubmitControls();
    const message = 'Unable to prepare your cart. Please try adding this item again.';
    this.dispatchEvent(new CartErrorEvent(form.getAttribute('id') || '', message, message));
    if (addToCartTextError) {
      addToCartTextError.classList.remove('hidden');
      const textNode = addToCartTextError.childNodes[2];
      if (textNode) textNode.textContent = message;
      else addToCartTextError.appendChild(document.createTextNode(message));
    }
    this.#setLiveRegionText(message);
    console.warn('[ProductForm] Cart preparation failed', error);
    return;
  }

  // parseAddPayloadData will return a no_op key / value pair if we need to hijack the request.
  // When this happens, we handle the full request series and resolve via offer-engine.js.
  if (attrPropContent?.no_op === true) {
    this.#handleAddSuccess(event);
    this.#enableSubmitControls();
    return;
  }

  // Remove temp input fields
  for (const [key] of Array.from(formData.entries())) {
    if (key.startsWith('attrs[') || key.startsWith('props[') || key.startsWith('ref[')) {
      formData.delete(key);
    }
  }

  // Upsert parsed line item properties
  Object.entries(attrPropContent?.properties || {}).forEach(([key, value]) => {
    if (value != null) {
      formData.set(`properties[${key}]`, value);
    }
  });

  // Upsert parsed cart attributes
  Object.entries(attrPropContent?.attributes || {}).forEach(([key, value]) => {
    if (value != null) {
      formData.set(`attributes[${key}]`, value);
    }
  });

  const cartItemsComponents = document.querySelectorAll('cart-items-component');
  const cartItemComponentsSectionIds = new Set(['offer-engine-json']);
  cartItemsComponents.forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      cartItemComponentsSectionIds.add(item.dataset.sectionId);
    }
  });
  formData.set('sections', Array.from(cartItemComponentsSectionIds).join(','));

  const fetchCfg = fetchConfig('javascript', { body: formData });

  fetch(Theme.routes.cart_add_url, {
    ...fetchCfg,
    headers: {
      ...fetchCfg.headers,
      Accept: 'text/html',
    },
  })
    .then((response) => response.json())
    .then(async (response) => {
      if (response.status) {
        this.dispatchEvent(
          new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
        );

        if (!addToCartTextError) return;
        addToCartTextError.classList.remove('hidden');

        // Reuse the text node if the user is spam-clicking
        const textNode = addToCartTextError.childNodes[2];
        if (textNode) {
          textNode.textContent = response.message;
        } else {
          const newTextNode = document.createTextNode(response.message);
          addToCartTextError.appendChild(newTextNode);
        }

        // Create or get existing error live region for screen readers
        this.#setLiveRegionText(response.message);

        this.#timeout = setTimeout(() => {
          if (!addToCartTextError) return;
          addToCartTextError.classList.add('hidden');

          // Clear the announcement
          this.#clearLiveRegionText();
        }, ERROR_MESSAGE_DISPLAY_DURATION);

        // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
        // because our back-end still adds the max allowed amount to the cart.
        this.dispatchEvent(
          new CartAddEvent({}, this.id, {
            didError: true,
            source: 'product-form-component',
            itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
            productId: this.dataset.productId,
          })
        );

        return;
      } else {
        const id = formData.get('id');

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        if (!id) throw new Error('Form ID is required');

        this.#handleAddSuccess(event);

        // Add aria-live region to inform screen readers that the item was added
        // Get the added text from any add-to-cart button
        const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
        if (anyAddToCartButton) {
          const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

          this.#setLiveRegionText(addedText);

          setTimeout(() => {
            this.#clearLiveRegionText();
          }, SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        // Fetch the updated cart to get the actual total quantity for this variant
        await this.#fetchAndUpdateCartQuantity();

        this.dispatchEvent(
          new CartAddEvent({}, id.toString(), {
            source: 'product-form-component',
            itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
            productId: this.dataset.productId,
            sections: response.sections,
          })
        );
      }
    })
    .catch((error) => {
      console.error(error);
    })
    .finally(() => {
      this.#enableSubmitControls();
      cartPerformance.measureFromEvent('add:user-action', event);
    });
}

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {{properties?: Record<string, string | boolean>}} item
   * @returns {boolean}
   */
  #isGiftCartItem(item) {
    const properties = item?.properties || {};
    return properties._is_gift === true || properties._is_gift === 'true'
      || properties._gift_item === true || properties._gift_item === 'true';
  }

  /**
   * @param {HTMLElement} display
   * @returns {number[]}
   */
  #parseGiftQualifierVariantIds(display) {
    return (display.dataset.giftQualifierVariantIds || '')
      .split('|')
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0);
  }

  /**
   * Updates buy-button gift display state from existing cart quantity plus the active form quantity.
   * @param {{cart?: Cart}} [options]
   */
  #syncGiftDisplay(options = {}) {
    if (options.cart?.items) {
      this.#cartItems = options.cart.items;
    }

    const displays = this.querySelectorAll('[data-content="gift-display"][data-location="buy-buttons"]');
    if (!displays.length) return;

    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    const activeQuantity = Number(quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
    const activeVariantId = Number(this.refs.variantId?.value) || 0;

    displays.forEach((display) => {
      if (!(display instanceof HTMLElement)) return;

      const qualifierVariantIds = this.#parseGiftQualifierVariantIds(display);
      const qualifierVariantIdSet = new Set(qualifierVariantIds);
      const cartQuantity = this.#cartItems
        .filter((item) => qualifierVariantIdSet.has(Number(item.variant_id)) && !this.#isGiftCartItem(item))
        .reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
      const formQuantity = qualifierVariantIdSet.has(activeVariantId) ? activeQuantity : 0;
      const effectiveQuantity = cartQuantity + formQuantity;

      const giftItems = display.querySelectorAll('[data-gift-display-item]');
      let unlockedCount = 0;

      giftItems.forEach((item) => {
        if (!(item instanceof HTMLElement)) return;

        const threshold = Number(item.dataset.giftThreshold) || 1;
        const isUnlocked = effectiveQuantity >= threshold;
        const delta = Math.max(threshold - effectiveQuantity, 0);
        const deltaEl = item.querySelector('[data-content="gift-threshold-delta"]');

        item.classList.toggle('gift--inactive', !isUnlocked);
        if (deltaEl) deltaEl.textContent = String(delta);
        if (isUnlocked) unlockedCount++;
      });

      const unlockedCountEl = display.querySelector('[data-content="gift-unlocked-count"]');
      const unlockedNumberEl = display.querySelector('[data-content="gift-unlocked-number"]');

      display.dataset.unlockedCount = String(unlockedCount);
      if (unlockedNumberEl) unlockedNumberEl.textContent = String(unlockedCount);
      unlockedCountEl?.classList.toggle('max--unlocked', unlockedCount === giftItems.length);
    });
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * Gets all add-to-cart containers owned by this product form.
   * @param {ParentNode} [root] - The root to query.
   * @returns {AddToCartComponent[]}
   */
  #getAddToCartButtonContainers(root = this) {
    return /** @type {AddToCartComponent[]} */ (Array.from(root.querySelectorAll('add-to-cart-component')));
  }

  /**
   * Identifies the submit role for matching buttons across variant renders.
   * @param {AddToCartComponent} container - The add-to-cart container.
   * @returns {string}
   */
  #getAddToCartRole(container) {
    return container.dataset.addToCartRole || 'standard';
  }

  /**
   * Finds this form's matching product form in freshly rendered variant HTML.
   * @param {Document | Element} html - The rendered variant HTML.
   * @returns {ProductFormComponent | HTMLElement | null}
   */
  #getMatchingRenderedProductForm(html) {
    const currentSectionId = this.dataset.sectionId || '';
    const currentProductId = this.dataset.productId || '';
    const currentVariantId = this.refs.variantId?.value || '';

    const forms = /** @type {Array<ProductFormComponent | HTMLElement>} */ (
      Array.from(html.querySelectorAll('product-form-component'))
    );

    if (!forms.length) return null;

    return forms.find((form) => {
      if (currentSectionId && form.dataset.sectionId !== currentSectionId) return false;
      if (currentProductId && form.dataset.productId !== currentProductId) return false;

      const renderedVariantId = form.querySelector('input[name="id"]')?.value || '';
      return !currentVariantId || !renderedVariantId || renderedVariantId === currentVariantId;
    }) || null;
  }

  /**
   * Updates add-to-cart submit button markup.
   * @param {Document | Element} html - The rendered variant HTML.
   */
  #morphAddToCartButtons(html) {
    const currentContainers = this.#getAddToCartButtonContainers();
    const renderedForm = this.#getMatchingRenderedProductForm(html);
    const newContainers = renderedForm ? this.#getAddToCartButtonContainers(renderedForm) : [];

    if (currentContainers.length === 0 || newContainers.length === 0) return;

    for (const currentContainer of currentContainers) {
      const currentButton = currentContainer.refs.addToCartButton;
      if (!currentButton) continue;

      const currentRole = this.#getAddToCartRole(currentContainer);
      const newContainer =
        newContainers.find((container) => this.#getAddToCartRole(container) === currentRole) ||
        (currentContainers.length === 1 ? newContainers[0] : undefined);
      const newButton = newContainer?.querySelector('[ref="addToCartButton"]');

      if (newButton) {
        morph(currentButton, newButton);
      }
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;

    // Update the variant ID
    variantId.value = event.detail.resource?.id ?? '';

    const { acceleratedCheckoutButtonContainer } = this.refs;
    const allAddToCartContainers = this.#getAddToCartButtonContainers();
    const currentPreorderChecked = this.#getPreorderToggleInput()?.checked;

    // Update state and text for add-to-cart button
    if (allAddToCartContainers.length === 0 && !acceleratedCheckoutButtonContainer) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      for (const container of allAddToCartContainers) {
        container.disable();
      }
    } else {
      for (const container of allAddToCartContainers) {
        container.enable();
      }
    }

    this.#morphAddToCartButtons(event.detail.data.html);

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        for (const container of this.#getAddToCartButtonContainers()) {
          container.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
        }
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    if (quantitySelector && event.detail.resource?.id != null) {
      quantitySelector.dataset.variantId = event.detail.resource.id.toString();
    }

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    let didMorphProductFormButtons = false;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);
        didMorphProductFormButtons = true;

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    if (!didMorphProductFormButtons) {
      this.#morphPreorderToggle(event.detail.data.html);
    }
    this.#morphGiftDisplay(event.detail.data.html);
    this.#syncPreorderControls({ preferredChecked: currentPreorderChecked });
    this.#syncGiftDisplay();
    this.#syncOfferPriceDisplay();

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;
    const hasGiftDisplay = this.querySelector('[data-content="gift-display"][data-location="buy-buttons"]') !== null;

    if (!hasB2BFeatures && !hasGiftDisplay) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    for (const container of this.#getAddToCartButtonContainers()) {
      container.disable();
    }
    this.#disablePreorderToggle();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
